import { Box, Typography, Grid } from '@mui/material';
import { styled } from '@mui/material/styles';
import { Link, useLocation } from 'react-router-dom';
import BusinessIcon from '@mui/icons-material/Business';
import ListAltIcon from '@mui/icons-material/ListAlt';
import CategoryIcon from '@mui/icons-material/Category';
import ArticleOutlinedIcon from '@mui/icons-material/ArticleOutlined';
import AdminPanelSettingsOutlinedIcon from '@mui/icons-material/AdminPanelSettingsOutlined';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';

const SettingsCard = styled(Link)(({ theme, active }) => ({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  padding: '20px 24px',
  borderRadius: 16,
  textDecoration: 'none',
  backgroundColor: active ? theme.palette.primary.main : theme.palette.background.paper,
  color: active ? '#fff' : theme.palette.text.primary,
  border: `1px solid ${active ? theme.palette.primary.main : theme.palette.divider}`,
  transition: 'all 200ms ease',
  boxShadow: active ? '0 8px 16px rgba(105, 203, 249, 0.3)' : 'none',
  '&:hover': {
    transform: 'translateY(-2px)',
    boxShadow: active
      ? '0 12px 24px rgba(105, 203, 249, 0.4)'
      : '0 8px 16px rgba(0, 0, 0, 0.08)',
    borderColor: theme.palette.primary.main,
    backgroundColor: active ? theme.palette.primary.main : 'rgba(105, 203, 249, 0.05)',
  },
}));

const IconWrapper = styled(Box)(({ theme, active }) => ({
  width: 48,
  height: 48,
  borderRadius: 12,
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  backgroundColor: active ? 'rgba(255, 255, 255, 0.2)' : theme.palette.primary.light,
  color: active ? '#fff' : theme.palette.primary.main,
  marginRight: 16,
}));

// Radiology-specific settings only. Cross-cutting items (Branches, Permissions,
// Audit Trail) live under the Settings "General" tab so they remain reachable
// on deployments that don't run radiology.
const settingsItems = [
  {
    id: 'exam-list',
    title: 'Exam List',
    description: 'Configure available examinations',
    path: 'exam-list',
    icon: ListAltIcon,
  },
  {
    id: 'exam-group',
    title: 'Exam Groups',
    description: 'Organize exams into categories',
    path: 'exam-group',
    icon: CategoryIcon,
  },
  {
    id: 'report-templates',
    title: 'Report Templates',
    description: 'Manage report templates for different exam types',
    path: 'report-templates',
    icon: ArticleOutlinedIcon,
  },
];

export default function RadiologySettingsCards() {
  const location = useLocation();

  return (
    <Box sx={{ maxWidth: 800 }}>
      <Typography
        variant="body1"
        color="text.secondary"
        sx={{ mb: 4 }}
      >
        Configure your clinic settings and preferences
      </Typography>

      <Grid container spacing={2}>
        {settingsItems.map((item) => {
          const Icon = item.icon;
          const isActive = location.pathname.includes(item.path);

          return (
            <Grid item xs={12} key={item.id}>
              <SettingsCard to={item.path} active={isActive ? 1 : 0}>
                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                  <IconWrapper active={isActive ? 1 : 0}>
                    <Icon />
                  </IconWrapper>
                  <Box>
                    <Typography
                      variant="h6"
                      sx={{
                        fontWeight: 600,
                        fontSize: '1.0625rem',
                        color: 'inherit',
                        mb: 0.25,
                      }}
                    >
                      {item.title}
                    </Typography>
                    <Typography
                      variant="body2"
                      sx={{
                        color: isActive ? 'rgba(255,255,255,0.8)' : 'text.secondary',
                        fontSize: '0.8125rem',
                      }}
                    >
                      {item.description}
                    </Typography>
                  </Box>
                </Box>
                <ChevronRightIcon
                  sx={{
                    color: isActive ? 'rgba(255,255,255,0.8)' : 'text.secondary',
                    fontSize: 24,
                  }}
                />
              </SettingsCard>
            </Grid>
          );
        })}
      </Grid>
    </Box>
  );
}
